/**
 * NotFound — the in-SPA 404 surface.
 *
 * Two delivery modes:
 *
 *   1. Hash-based SPA fallback. When a route cannot be resolved
 *      (e.g. /#scene=unknown), this component renders inside the
 *      existing root and surfaces a compact "scene not found" toast
 *      inside the HUD layer. Most user-facing 404s live here.
 *
 *   2. Standalone 404 page. The same content is statically rendered
 *      at /404.html (see public/404.html) for direct hits to unknown
 *      paths when OpenResty is configured to serve it as the
 *      fallback page. Both share the same fonts + colours.
 *
 * Style: dark bg, big local-display "404", sentence-styled body, and a
 * link back to "/" with a left arrow. No external assets.
 */

import { useEffect } from 'react';
import './not-found.css';

export interface NotFoundProps {
  /** Optional headline override. */
  title?: string;
  /** Optional body copy. */
  body?: string;
  /** Optional destination for the back link. */
  href?: string;
  /** Optional toast timeout in ms. If 0 or undefined, no toast. */
  toastMs?: number;
  /** Called after the toast timeout fires (if it did). */
  onToastDone?: () => void;
}

const DEFAULTS = {
  title: '404',
  body: 'this planet doesn’t have that area yet',
  href: '/',
} as const;

export function NotFound({
  title = DEFAULTS.title,
  body = DEFAULTS.body,
  href = DEFAULTS.href,
  toastMs = 3000,
  onToastDone,
}: NotFoundProps) {
  // For the toast variant — auto-dismiss after `toastMs`.
  useEffect(() => {
    if (!toastMs) return;
    const id = window.setTimeout(() => onToastDone?.(), toastMs);
    return () => window.clearTimeout(id);
  }, [toastMs, onToastDone]);

  const isToast = !!toastMs;
  return (
    <div className={['not-found', isToast ? 'not-found--toast' : ''].join(' ')}>
      <div className="not-found__inner">
        <p className="not-found__code">{title}</p>
        <p className="not-found__body">{body}</p>
        <a className="not-found__back" href={href}>
          <span aria-hidden="true" className="not-found__back-arrow">
            &larr;
          </span>
          <span>back to the planet</span>
        </a>
      </div>
    </div>
  );
}

export default NotFound;
